let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import * as LocalAuthentication from 'expo-local-authentication';
import { googleSignIn } from '../../utils/googleAuth';
import * as Apple from 'expo-apple-authentication';
import { Ionicons, FontAwesome } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { CommonActions } from '@react-navigation/native';
import { useAuth } from '../../context/AuthContext';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

const API_BASE_URL = 'https://api.jobeng.ai';


// ✅ SAFE STRING NORMALIZER (prevents: email.trim is not a function)
const s = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

/**
 * Persist the user's identity to ALL canonical AsyncStorage keys.
 * Without this, downstream screens that look up `user_id` (for uploads, etc.)
 * cannot resolve the numeric user ID and operations fail with 403s.
 *
 * MUST be called from every login path: email/password, Google SSO, Apple SSO,
 * biometric, and fallback flows.
 */
const persistUserIdentity = async (user, token) => {
  try {
    if (token) {
      // Write to BOTH canonical token keys. 64 downstream screens read
      // 'userToken' while only 'auth_token' was being written previously,
      // which caused those screens to make unauthenticated requests after a
      // fresh install + login. See audit report.
      await AsyncStorage.setItem('auth_token', token);
      await AsyncStorage.setItem('userToken', token);
    }
    if (user) {
      const userJson = typeof user === 'string' ? user : JSON.stringify(user);
      await AsyncStorage.setItem('userData', userJson);
      await AsyncStorage.setItem('user', userJson);

      // Numeric user ID — the critical piece for the documents upload endpoint
      const uid = user?.id || user?.user_id || user?.userId;
      if (uid != null) {
        const uidStr = String(uid);
        await AsyncStorage.setItem('user_id', uidStr);
        await AsyncStorage.setItem('userId', uidStr);
        await AsyncStorage.setItem('professional_user_id', uidStr);
      }

      // Email for past_chats / dashboard sync
      if (user?.email) {
        await AsyncStorage.setItem('userEmail', String(user.email));
      }
    }
  } catch (e) {
    console.error('persistUserIdentity error:', e);
  }
};

export default function ProfessionalLoginScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  const [isLoading, setIsLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('');
  const [hasSavedCredentials, setHasSavedCredentials] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [appleAuthAvailable, setAppleAuthAvailable] = useState(false);



  useEffect(() => {
    checkBiometricAvailability();
    checkSavedCredentials();
    loadSavedData();
    checkAppleAuthAvailability();
  }, []);


  // ✅ reset helper (fixes reset payload/index issues)
  // ── Sync auth user data → ProfileScreen shared storage ──
  const syncAuthToProfile = async (user) => {
    try {
      const existing = await AsyncStorage.getItem(`professional_${_profUserId}_userProfile`);
      const profile = existing ? JSON.parse(existing) : {};
      const fullName = [user?.first_name || user?.firstName || '', user?.last_name || user?.lastName || ''].filter(Boolean).join(' ') || user?.name || user?.full_name || '';
      profile.personal = {
        ...(profile.personal || {}),
        fullName: fullName || profile?.personal?.fullName || '',
        email: user?.email || profile?.personal?.email || '',
        phone: user?.phone || profile?.personal?.phone || '',
        jobTitle: user?.job_title || user?.jobTitle || user?.position || profile?.personal?.jobTitle || '',
        company: user?.company || profile?.personal?.company || '',
        location: user?.location || profile?.personal?.location || '',
        profileImage: user?.profile_image || user?.avatar || profile?.personal?.profileImage || null,
        availability: profile?.personal?.availability || 'Available',
      };
      await AsyncStorage.setItem(`professional_${_profUserId}_userProfile`, JSON.stringify(profile));
      // Also write to dashboardData for HomeScreen
      const dash = { user: { fullName: profile.personal.fullName, email: profile.personal.email, jobTitle: profile.personal.jobTitle, profileImage: profile.personal.profileImage, availability: profile.personal.availability }, lastUpdated: new Date().toISOString() };
      await AsyncStorage.setItem('dashboardData', JSON.stringify(dash));
    } catch (e) {
      console.warn('syncAuthToProfile error:', e?.message);
    }
  };

    const resetToWelcome = () => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: 'WelcomeProfessionalScreen' }],
      })
    );
  };

  const loadSavedData = async () => {
    try {
      const savedEmail = await AsyncStorage.getItem(`professional_${_profUserId}_saved_professional_email`);
      const rememberLogin = await AsyncStorage.getItem(`professional_${_profUserId}_remember_professional_login`);

      if (savedEmail && rememberLogin === 'true') {
        setEmail(String(savedEmail));
        setRememberMe(true);
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  };

  const checkBiometricAvailability = async () => {
    try {
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();

      const available = hasHardware && isEnrolled;
      setBiometricAvailable(available);

      if (available && supportedTypes.length > 0) {
        if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
          setBiometricType('Face ID');
        } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
          setBiometricType('Touch ID');
        } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
          setBiometricType('Iris');
        } else {
          setBiometricType('Biometric');
        }
      }

      console.log('✅ Biometric availability checked:', { available, type: biometricType });
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      setBiometricAvailable(false);
    }
  };

  // ✅ NEW: Check Apple Authentication Availability
  const checkAppleAuthAvailability = async () => {
    try {
      const isAvailable = await Apple.isAvailableAsync();
      setAppleAuthAvailable(isAvailable);
      console.log('✅ Apple Auth availability:', isAvailable);
    } catch (error) {
      console.error('Error checking Apple auth availability:', error);
      setAppleAuthAvailable(false);
    }
  };

  const checkSavedCredentials = async () => {
    try {
      const savedCredentials = await AsyncStorage.getItem('biometric_credentials_professional');
      setHasSavedCredentials(!!savedCredentials);
    } catch (error) {
      console.error('Error checking saved credentials:', error);
    }
  };

  const handleLogin = async () => {
    // Clear stale per-user cache BEFORE login
    try {
      await AsyncStorage.multiRemove([
        // Identity — critical, these are read by many screens
        'user_id','userId','professional_user_id','business_user_id',
        // Tokens
        'auth_token','userToken','token','refresh_token',
        // Professional-scoped profile
        'prof_user_profile','prof_dashboard_data','userProfile',
        'professional_default_userProfile','profileStats','dashboardData',
        // Business-scoped (clear if switching from business)
        'company_profile','company_logo','company_id','companyProfile','companyLogo',
        'business_user_profile','businessUserProfile',
        // Session bundles
        'userData','user','currentUser',
        'applied_jobs_guest',
      ]);
    } catch {}

    const normalizedEmail = s(email).trim().toLowerCase();
    const normalizedPassword = s(password).trim();

    if (!normalizedEmail || !normalizedPassword) {
      Alert.alert('Error', 'Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Attempting professional login with data:', {
        email: normalizedEmail,
        password: '***hidden***',
        user_type: 'professional',
      });

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          password: normalizedPassword,
          user_type: 'professional',
          remember_me: rememberMe,
        }),
      });

      const responseText = await response.text();
      console.log('Login response status:', response.status);
      console.log('Raw login response:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Invalid response format from server');
      }

      console.log('Login response:', data);

      if (response.ok && data.success) {
        // Parse response data
        let userData = null;
        let token = null;

        if (data.data && data.data.user) {
          userData = data.data.user;
          token = data.data.token;
        } else if (data.user) {
          userData = data.user;
          token = data.token;
        }

        if (!userData) {
          throw new Error('Invalid response format: user data not found');
        }

        // Ensure user type is professional
        userData.user_type = 'professional';

        // Store all canonical identity keys (token + numeric user_id + user JSON)
        await persistUserIdentity(userData, token);
        if (token) {
          await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, JSON.stringify(userData));
        }

        // Save biometric credentials if biometric is available and remember me is enabled
        if (biometricAvailable && rememberMe) {
          await saveBiometricCredentials(token, userData.id, normalizedEmail);
        }

        // Save login preferences
        if (rememberMe) {
          await AsyncStorage.setItem(`professional_${_profUserId}_saved_professional_email`, normalizedEmail);
          await AsyncStorage.setItem(`professional_${_profUserId}_remember_professional_login`, 'true');
        } else {
          await AsyncStorage.removeItem(`professional_${_profUserId}_saved_professional_email`);
          await AsyncStorage.removeItem(`professional_${_profUserId}_remember_professional_login`);
        }

        // ✅ Ensure AuthContext updated
        if (login && token) {
          await login(userData, token);
        }

        console.log('✅ Professional login successful, resetting to: WelcomeProfessionalScreen');
        await syncAuthToProfile(userData);
        resetToWelcome();
      } else {
        let errorMessage = 'Login failed. Please try again.';

        if (data && data.error) {
          errorMessage = data.error;
        } else if (data && data.message) {
          errorMessage = data.message;
        } else if (response.status === 401) {
          errorMessage = 'Invalid email or password.';
        } else if (response.status === 422) {
          errorMessage = 'Please check your input data.';
        }

        Alert.alert('Login Error', errorMessage);
      }
    } catch (error) {
      console.error('Professional login error:', error);

      let userMessage = 'Login failed. Please try again.';

      if (String(error?.message || '').includes('Network request failed')) {
        userMessage = 'Network error. Please check your internet connection.';
      } else if (error.message) {
        userMessage = error.message;
      }

      Alert.alert('Login Error', userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Google Auth Success Handler
  const handleGoogleAuthSuccess = async (authResult) => {
    setIsLoading(true);
    try {
      console.log('🔐 Processing Google auth success for professional');

      if (!authResult || !authResult.accessToken) {
        throw new Error('Invalid Google authentication result');
      }

      // First try to get user info from Google
      let userInfo = null;
      try {
        const userInfoResponse = await fetch(
          `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${authResult.accessToken}`
        );
        userInfo = await userInfoResponse.json();
      } catch (userInfoError) {
        console.warn('Could not fetch Google user info:', userInfoError);
      }

      // Try server validation first
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/google-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: authResult.accessToken,
            id_token: authResult.idToken,
            user_info: userInfo,
            user_type: 'professional',
          }),
        });

        const result = await response.json();

        if (result.success) {
          const token = result.data?.token;
          const user = result.data?.user;

          // Store authentication data (all canonical keys + numeric user_id)
          await persistUserIdentity(user, token);
          if (user) await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, JSON.stringify(user));

          // Save login preferences if remember me is enabled
          if (rememberMe && user?.email) {
            await AsyncStorage.setItem(`professional_${_profUserId}_saved_professional_email`, String(user.email));
            await AsyncStorage.setItem(`professional_${_profUserId}_remember_professional_login`, 'true');
          }

          // ✅ Ensure AuthContext updated
          if (login && token && user) {
            user.user_type = 'professional';
            await login(user, token);
          }

          console.log('✅ Google login successful for professional');
          await syncAuthToProfile(userData);
          resetToWelcome();
          return; // Success, exit function
        }
      } catch (serverError) {
        console.warn('Server Google login failed, using fallback:', serverError);
      }

      // ✅ FALLBACK: If server validation fails, create local session
      if (userInfo && userInfo.email) {
        const fallbackUser = {
          id: userInfo.id || Date.now().toString(),
          email: userInfo.email,
          name: userInfo.name || 'Google Professional User',
          full_name: userInfo.name || 'Google Professional User',
          picture: userInfo.picture,
          user_type: 'professional',
          loginMethod: 'google',
        };

        const fallbackToken = 'google_professional_fallback_' + Date.now();

        // Store fallback authentication data (all canonical keys + numeric user_id)
        await persistUserIdentity(fallbackUser, fallbackToken);
        await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, JSON.stringify(fallbackUser));

        // Save login preferences
        if (rememberMe) {
          await AsyncStorage.setItem(`professional_${_profUserId}_saved_professional_email`, String(fallbackUser.email));
          await AsyncStorage.setItem(`professional_${_profUserId}_remember_professional_login`, 'true');
        }

        // ✅ Ensure AuthContext updated
        if (login) {
          await login(fallbackUser, fallbackToken);
        }

        console.log('✅ Google fallback login successful for professional');
        await syncAuthToProfile(userData);
        resetToWelcome();
      } else {
        throw new Error('Unable to get user information from Google');
      }
    } catch (error) {
      console.error('❌ Google auth success error:', error);
      Alert.alert('Google Login Error', 'Unable to complete Google login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Google Login Handler
  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      console.log('🔐 Google login attempt for professional');


      // Native Google Sign-In (returns { accessToken, idToken })
      const authentication = await googleSignIn();
      await handleGoogleAuthSuccess(authentication);
    } catch (error) {
      if (error?.cancelled) return;
      console.error('❌ Google login error:', error);
      Alert.alert('Google Login Error', 'Unable to login with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Apple Login Implementation
  const handleAppleLogin = async () => {
    if (!appleAuthAvailable) {
      Alert.alert('Apple Login', 'Apple Sign In is not available on this device');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🍎 Apple login attempt for professional');

      const credential = await Apple.signInAsync({
        requestedScopes: [Apple.AppleAuthenticationScope.FULL_NAME, Apple.AppleAuthenticationScope.EMAIL],
      });

      if (credential.identityToken) {
        console.log('🍎 Apple credential received:', {
          user: credential.user,
          email: credential.email,
          fullName: credential.fullName,
          identityToken: credential.identityToken ? 'present' : 'missing',
        });

        // Try server validation first
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/apple-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              identity_token: credential.identityToken,
              authorization_code: credential.authorizationCode,
              user_type: 'professional',
              email: credential.email,
              full_name: credential.fullName
                ? `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim()
                : null,
              user_info: {
                id: credential.user,
                email: credential.email,
                name: credential.fullName
                  ? `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim()
                  : 'Apple Professional User',
                firstName: credential.fullName?.givenName,
                lastName: credential.fullName?.familyName,
              },
            }),
          });

          const result = await response.json();

          if (result.success) {
            const token = result.data?.token;
            const user = result.data?.user;

            // Store authentication data (all canonical keys + numeric user_id)
            await persistUserIdentity(user, token);
            if (user) await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, JSON.stringify(user));

            // Save login preferences if remember me is enabled
            if (rememberMe && user?.email) {
              await AsyncStorage.setItem(`professional_${_profUserId}_saved_professional_email`, String(user.email));
              await AsyncStorage.setItem(`professional_${_profUserId}_remember_professional_login`, 'true');
            }

            // ✅ Ensure AuthContext updated
            if (login && token && user) {
              user.user_type = 'professional';
              await login(user, token);
            }

            console.log('✅ Apple server login successful for professional');
            await syncAuthToProfile(userData);
            resetToWelcome();
            return; // Success, exit function
          }
        } catch (serverError) {
          console.warn('Server Apple login failed, using fallback:', serverError);
        }

        // ✅ FALLBACK: If server validation fails, create local session
        const fallbackUser = {
          id: credential.user,
          email: credential.email || 'apple_professional_user@privaterelay.appleid.com',
          name: credential.fullName
            ? `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim()
            : 'Apple Professional User',
          full_name: credential.fullName
            ? `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim()
            : 'Apple Professional User',
          first_name: credential.fullName?.givenName || 'Apple',
          last_name: credential.fullName?.familyName || 'User',
          user_type: 'professional',
          loginMethod: 'apple',
        };

        const fallbackToken = 'apple_professional_' + Date.now();

        // Store fallback authentication data (all canonical keys + numeric user_id)
        await persistUserIdentity(fallbackUser, fallbackToken);
        await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, JSON.stringify(fallbackUser));

        // Save login preferences
        if (rememberMe) {
          await AsyncStorage.setItem(`professional_${_profUserId}_saved_professional_email`, String(fallbackUser.email));
          await AsyncStorage.setItem(`professional_${_profUserId}_remember_professional_login`, 'true');
        }

        // ✅ Ensure AuthContext updated
        if (login) {
          await login(fallbackUser, fallbackToken);
        }

        console.log('✅ Apple fallback login successful for professional');
        await syncAuthToProfile(userData);
        resetToWelcome();
      }
    } catch (error) {
      console.error('❌ Apple login error:', error);
      if (error.code !== 'ERR_CANCELED') {
        Alert.alert('Apple Login Error', 'Failed to complete Apple login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Biometric Login
  const handleBiometricLogin = async () => {
    if (!biometricAvailable) {
      Alert.alert('Unavailable', 'Biometric authentication not available on this device');
      return;
    }

    if (!hasSavedCredentials) {
      Alert.alert(
        'No Saved Credentials',
        'Please login with email and password first and enable "Remember Me" to use biometric login'
      );
      return;
    }

    try {
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: `Login with ${biometricType}`,
        cancelLabel: 'Cancel',
        fallbackLabel: 'Use Password',
        disableDeviceFallback: false,
      });

      if (result.success) {
        setIsLoading(true);

        // Check if we have saved biometric credentials
        const savedCredentials = await AsyncStorage.getItem('biometric_credentials_professional');
        if (!savedCredentials) {
          Alert.alert('Biometric Login', 'No saved biometric credentials found. Please login with email/password first.');
          return;
        }

        const credentials = JSON.parse(savedCredentials);

        // Try to validate biometric credentials with server
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/biometric-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              biometric_token: credentials.token,
              user_id: credentials.user_id,
              device_id: credentials.device_id || 'unknown',
              user_type: 'professional',
            }),
          });

          const serverResult = await response.json();

          if (serverResult.success) {
            const token = serverResult.data?.token;
            const user = serverResult.data?.user;

            // Store authentication data (all canonical keys + numeric user_id)
            await persistUserIdentity(user, token);
            if (user) await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, JSON.stringify(user));

            // ✅ Ensure AuthContext updated
            if (login && token && user) {
              user.user_type = 'professional';
              await login(user, token);
            }

            console.log('✅ Biometric login successful for professional');
            await syncAuthToProfile(userData);
            resetToWelcome();
            return; // Success, exit function
          }
        } catch (serverError) {
          console.warn('Server biometric validation failed, using fallback:', serverError);
        }

        // ✅ FALLBACK: If server validation fails, use stored credentials
        if (credentials.email) {
          const fallbackUser = {
            id: credentials.user_id,
            email: credentials.email,
            name: credentials.name || 'Biometric Professional User',
            full_name: credentials.name || 'Biometric Professional User',
            user_type: 'professional',
            loginMethod: 'biometric',
          };

          const fallbackToken = credentials.token || ('biometric_professional_' + Date.now());

          // Store fallback authentication data (all canonical keys + numeric user_id)
          await persistUserIdentity(fallbackUser, fallbackToken);
          await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, JSON.stringify(fallbackUser));

          // ✅ Ensure AuthContext updated
          if (login) {
            await login(fallbackUser, fallbackToken);
          }

          console.log('✅ Biometric fallback login successful for professional');
          await syncAuthToProfile(userData);
          resetToWelcome();
        } else {
          throw new Error('Invalid biometric credentials');
        }
      } else {
        if (result.error && result.error !== 'UserCancel') {
          Alert.alert('Authentication Failed', 'Biometric authentication not recognized');
        }
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      Alert.alert('Biometric Login Error', 'Please try again or use email and password');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Save biometric credentials
  const saveBiometricCredentials = async (userToken, userId, userEmail) => {
    try {
      if (biometricAvailable && rememberMe) {
        const biometricCredentials = {
          token: userToken,
          user_id: userId,
          email: s(userEmail).trim().toLowerCase(),
          name: 'Professional User',
          device_id: 'device_professional_' + Date.now(),
          created_at: new Date().toISOString(),
        };

        await AsyncStorage.setItem('biometric_credentials_professional', JSON.stringify(biometricCredentials));
        setHasSavedCredentials(true);
        console.log('✅ Biometric credentials saved for professional');
      }
    } catch (error) {
      console.error('❌ Error saving biometric credentials:', error);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.keyboardAvoid}>
        {/* === BACK BUTTON ADDED HERE === */}
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back-outline" size={28} color="#007AFF" />
        </TouchableOpacity>
        {/* ============================== */}

        <ScrollView contentContainerStyle={styles.scrollContainer} showsVerticalScrollIndicator={false}>
          <View style={styles.container}>
            <View style={styles.headerContainer}>
              <Image source={require('../../../assets/jobeng_full_logo.png')} style={styles.logo} resizeMode="contain" />
              <Text style={styles.subtitle}>UK Engineering Excellence</Text>
            </View>

            <View style={styles.formContainer}>
              <Text style={styles.title}>Professional Login</Text>
              <Text style={styles.description}>Access your professional engineering network</Text>

              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.email && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="mail-outline"
                    size={20}
                    color={isFocused.email ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Professional Email"
                    placeholderTextColor="#8E8E93"
                    value={String(email ?? '')}
                    onChangeText={(t) => setEmail(String(t ?? ''))}
                    onFocus={() => setIsFocused((prev) => ({ ...prev, email: true }))}
                    onBlur={() => setIsFocused((prev) => ({ ...prev, email: false }))}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                </View>
              </View>

              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.password && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="lock-closed-outline"
                    size={20}
                    color={isFocused.password ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Password"
                    placeholderTextColor="#8E8E93"
                    value={String(password ?? '')}
                    onChangeText={(t) => setPassword(String(t ?? ''))}
                    onFocus={() => setIsFocused((prev) => ({ ...prev, password: true }))}
                    onBlur={() => setIsFocused((prev) => ({ ...prev, password: false }))}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity
                    onPress={() => setShowPassword((v) => !v)}
                    style={styles.eyeButton}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons
                      name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                      size={20}
                      color="#8E8E93"
                    />
                  </TouchableOpacity>
                </View>
              </View>

              {/* ✅ Forgot Password — gracefully degrades if ForgotPassword
                  screen is not registered in the navigator. */}
              <TouchableOpacity
                onPress={() => {
                  try {
                    const state = navigation.getState && navigation.getState();
                    const allRoutes = [];
                    const collect = (s) => {
                      if (!s || !s.routeNames) return;
                      s.routeNames.forEach(n => allRoutes.push(n));
                      (s.routes || []).forEach(r => r.state && collect(r.state));
                    };
                    collect(state);
                    if (allRoutes.includes('ForgotPassword')) {
                      navigation.navigate('ForgotPassword', { userType: 'professional' });
                      return;
                    }
                  } catch {}
                  Alert.alert(
                    'Reset your password',
                    'Email support@jobeng.ai from the address you signed up with and our team will reset your password for you.',
                    [{ text: 'OK' }],
                  );
                }}
                style={{ alignSelf: 'flex-end', marginBottom: 12 }}
                disabled={isLoading}
              >
                <Text style={{ color: '#007AFF', fontWeight: '600' }}>Forgot password?</Text>
              </TouchableOpacity>

              {/* Remember Me */}
              <View style={styles.rememberMeContainer}>
                <TouchableOpacity style={styles.rememberMeButton} onPress={() => setRememberMe(!rememberMe)}>
                  <Ionicons name={rememberMe ? 'checkbox' : 'square-outline'} size={20} color="#007AFF" />
                  <Text style={styles.rememberMeText}>Remember Me</Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity
                style={[styles.loginButton, isLoading && styles.loginButtonDisabled]}
                onPress={handleLogin}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator size={20} color="#FFFFFF" />
                ) : (
                  <>
                    <Text style={styles.loginButtonText}>Login to Professional Network</Text>
                    <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
                  </>
                )}
              </TouchableOpacity>

              {/* Biometric Login */}
              {biometricAvailable && hasSavedCredentials && (
                <TouchableOpacity style={styles.biometricButton} onPress={handleBiometricLogin} disabled={isLoading}>
                  <Ionicons name={biometricType === 'Face ID' ? 'scan' : 'finger-print'} size={24} color="#007AFF" />
                  <Text style={styles.biometricButtonText}>Login with {biometricType}</Text>
                </TouchableOpacity>
              )}


              <View style={styles.footerContainer}>
                <Text style={styles.footerText}>
                  Don't have an account?{' '}
                  <Text style={styles.footerLink} onPress={() => navigation.navigate('ProfessionalSignup')}>
                    Sign Up
                  </Text>
                </Text>

              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  /* === BACK BUTTON STYLE ADDED HERE === */
  backButton: {
    position: 'absolute',
    top: 10,
    left: 20,
    zIndex: 10,
    padding: 5, // Increases the touchable area
  },
  /* ==================================== */
  keyboardAvoid: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 40,
  },
  headerContainer: {
    alignItems: 'center',
    marginBottom: 40,
    marginTop: 30, // Added margin to avoid overlap with back button
  },
  logo: {
    width: 120,
    height: 60,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: '#8E8E93',
    fontWeight: '500',
  },
  formContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    textAlign: 'center',
    marginBottom: 8,
  },
  description: {
    fontSize: 16,
    color: '#8E8E93',
    textAlign: 'center',
    marginBottom: 32,
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    paddingHorizontal: 16,
    height: 50,
  },
  inputWrapperFocused: {
    borderColor: '#007AFF',
    backgroundColor: '#FFFFFF',
  },
  inputIcon: {
    marginRight: 12,
  },
  eyeButton: {
    paddingLeft: 8,
    paddingVertical: 4,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1C1C1E',
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  rememberMeButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rememberMeText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#1C1C1E',
  },
  loginButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  loginButtonDisabled: {
    opacity: 0.7,
  },
  loginButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 8,
  },
  biometricButton: {
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  biometricButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#007AFF',
    marginLeft: 8,
  },
  socialLoginContainer: {
    marginBottom: 24,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E5E5EA',
  },
  dividerText: {
    marginHorizontal: 16,
    fontSize: 14,
    color: '#8E8E93',
  },
  socialButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  socialButton: {
    flex: 1,
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 4,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  socialButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1C1C1E',
    marginLeft: 8,
  },
  footerContainer: {
    alignItems: 'center',
  },
  footerText: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 16,
  },
  footerLink: {
    color: '#007AFF',
    fontWeight: '500',
  },
  switchModeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  switchModeText: {
    fontSize: 14,
    color: '#007AFF',
    marginLeft: 4,
  },
});
